import React, {
  useEffect,
  useState,
  useId,
  useCallback,
  memo,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  SVGIconProps,
  IconColor,
  IconSize,
  IconBackground,
} from './SVGIcon.types';
import { checkColorExists } from './helper';

const getColorClassOrHex = (
  color: IconColor | IconBackground | string | undefined,
  prefix: 'fill' | 'bg' | 'border' = 'fill',
): string => {
  if (!color || color === 'none') return '';

  if (typeof color === 'string' && color.startsWith('#')) {
    return color;
  }

  const colorExists = checkColorExists(color as string);
  if (colorExists) {
    return `${prefix}-${color}`;
  }

  return '';
};

/**
 * Mapuje rozmiary na klasy Tailwind
 * @param size - rozmiar ikony
 * @returns klasa Tailwind dla rozmiaru
 */
const getSizeClass = (size: IconSize | undefined): string => {
  if (!size) return 'w-6 h-6'; // domyślny rozmiar (md)

  if (typeof size === 'number') {
    return `w-[${size}px] h-[${size}px]`;
  }

  if (typeof size === 'string' && size.includes('px')) {
    return `w-[${size}] h-[${size}]`;
  }

  const sizeMap: Record<string, string> = {
    xs: 'w-3 h-3', // 12px
    sm: 'w-4 h-4', // 16px
    md: 'w-6 h-6', // 24px (domyślny)
    lg: 'w-8 h-8', // 32px
    xl: 'w-12 h-12', // 48px
    '2xl': 'w-16 h-16', // 64px
  };

  return sizeMap[size] || 'w-6 h-6';
};

/**
 * Mapuje właściwość rounded na klasy Tailwind
 * @param rounded - wartość rounded
 * @returns klasa Tailwind dla rounded
 */
const getRoundedClass = (rounded: boolean | string | undefined): string => {
  if (!rounded) return '';

  if (rounded === true) return 'rounded';

  const roundedMap: Record<string, string> = {
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  };

  return roundedMap[rounded as string] || 'rounded';
};

/**
 * Mapuje właściwość shadow na klasy Tailwind
 * @param shadow - wartość shadow
 * @returns klasa Tailwind dla shadow
 */
const getShadowClass = (shadow: boolean | string | undefined): string => {
  if (!shadow) return '';

  if (shadow === true) return 'shadow';

  const shadowMap: Record<string, string> = {
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
  };

  return shadowMap[shadow as string] || 'shadow';
};

/**
 * Uniwersalny komponent SVG ikon z pełną obsługą kolorów z APP_COLORS
 *
 * @example
 * ```tsx
 * <SVGIcon
 *   name="check"
 *   size="lg"
 *   fill="primary"
 *   background="gray-100"
 *   onClick={handleClick}
 * />
 * ```
 */
export const SVGIcon = memo<SVGIconProps>(
  ({
    name,
    size = 'md',
    width,
    height,
    fill,
    background = 'none',
    // Backward compatibility
    fillColor,
    color,
    states,
    className,
    rounded,
    shadow,
    border,
    borderColor,
    spin,
    pulse,
    bounce,
    'aria-label': ariaLabel,
    'aria-hidden': ariaHidden,
    onClick,
    onMouseEnter,
    onMouseLeave,
  }) => {
    const [IconComponent, setIconComponent] = useState<React.FC<
      React.SVGProps<SVGSVGElement>
    > | null>(null);
    const [loadError, setLoadError] = useState<boolean>(false);
    const componentId = useId().replace(/:/g, '');
    const importIcon = useCallback(async () => {
      try {
        setLoadError(false);
        /* @vite-ignore */
        if (name) {
          const imported = await import(`./svg/${name}.svg`);
          setIconComponent(() => imported.ReactComponent);
        }
      } catch (error) {
        console.error(
          `SVGIcon: Błąd podczas importowania ikony '${name}':`,
          error,
        );
        setLoadError(true);
      }
    }, [name]);

    useEffect(() => {
      importIcon();
    }, [importIcon]);

    // Jeśli size jest liczbą, użyj jako width/height (chyba że podano jawnie)
    const effectiveWidth = width ?? (typeof size === 'number' ? size : undefined);
    const effectiveHeight = height ?? (typeof size === 'number' ? size : undefined);

    // Backward compatibility - priorytet dla nowych właściwości
    // UWAGA: Tylko kolory z APP_COLORS są obsługiwane
    const finalFill = fill || fillColor || color;

    // Ostrzeżenie o deprecated props
    useEffect(() => {
      if (fillColor) {
        console.warn(
          'SVGIcon: Właściwość "fillColor" jest deprecated. Użyj "fill".',
        );
      }
      if (color) {
        console.warn(
          'SVGIcon: Właściwość "color" jest deprecated. Użyj "fill".',
        );
      }
    }, [fillColor, color]);

    // Budowanie klas CSS
    const baseClasses = [
      'inline-flex items-center justify-center',
      'transition-all duration-200 ease-in-out',
    ];

    // Rozmiar
    const sizeClasses = effectiveWidth || effectiveHeight ? '' : getSizeClass(size);

    // Kolory
    const fillClass = getColorClassOrHex(finalFill as IconColor, 'fill');
    const useStyleInline = useMemo(() => {
      if (fillClass.startsWith('#')) return true;
      else return false;
    }, [fillClass]);
    const backgroundClass =
      background !== 'none' ? getColorClassOrHex(background, 'bg') : '';

    // Dodatkowe style
    const roundedClass = getRoundedClass(rounded);
    const shadowClass = getShadowClass(shadow);
    const borderClass = border ? 'border' : '';
    const borderColorClass = borderColor
      ? getColorClassOrHex(borderColor, 'border')
      : '';

    // Animacje
    const animationClasses = [
      spin && 'animate-spin',
      pulse && 'animate-pulse',
      bounce && 'animate-bounce',
    ].filter(Boolean);

    // Stany hover/active/focus
    const stateClasses = [];
    if (states?.hover) {
      if (states.hover.fill)
        stateClasses.push(
          `hover:${getColorClassOrHex(states.hover.fill, 'fill')}`,
        );
      if (states.hover.background)
        stateClasses.push(
          `hover:${getColorClassOrHex(states.hover.background, 'bg')}`,
        );
      if (states.hover.scale)
        stateClasses.push(
          `hover:scale-${Math.round(states.hover.scale * 100)}`,
        );
      if (states.hover.opacity)
        stateClasses.push(
          `hover:opacity-${Math.round(states.hover.opacity * 100)}`,
        );
    }

    if (states?.active) {
      if (states.active.fill)
        stateClasses.push(
          `active:${getColorClassOrHex(states.active.fill, 'fill')}`,
        );
      if (states.active.background)
        stateClasses.push(
          `active:${getColorClassOrHex(states.active.background, 'bg')}`,
        );
      if (states.active.scale)
        stateClasses.push(
          `active:scale-${Math.round(states.active.scale * 100)}`,
        );
      if (states.active.opacity)
        stateClasses.push(
          `active:opacity-${Math.round(states.active.opacity * 100)}`,
        );
    }

    // if (states?.focus) {
    //   if (states.focus.fill)
    //     stateClasses.push(`focus:${getColorClassOrHex(states.focus.fill, 'fill')}`);
    //   if (states.focus.background)
    //     stateClasses.push(
    //       `focus:${getColorClassOrHex(states.focus.background, 'bg')}`,
    //     );
    //   if (states.focus.outline)
    //     stateClasses.push('focus:outline-2 focus:outline-primary');
    // }

    // Łączenie wszystkich klas z clsx
    const wrapperClasses = clsx(
      baseClasses,
      sizeClasses,
      backgroundClass,
      roundedClass,
      shadowClass,
      borderClass,
      borderColorClass,
      animationClasses,
      stateClasses,

      {
        'cursor-pointer': !!onClick,
        'focus:outline-2 focus:outline-offset-2 focus:outline-primary':
          !!onClick,
      },
      className,
    );

    // Style inline tylko dla custom rozmiarów
    const inlineStyles: React.CSSProperties = {};

    if (effectiveWidth) {
      inlineStyles.width = typeof effectiveWidth === 'number' ? `${effectiveWidth}px` : effectiveWidth;
    }
    if (effectiveHeight) {
      inlineStyles.height = typeof effectiveHeight === 'number' ? `${effectiveHeight}px` : effectiveHeight;
    }

    // Error state
    if (loadError) {
      return (
        <div
          className={clsx(
            'inline-flex justify-center items-center',
            'text-red-600 bg-red-100 rounded border border-red-300',
            getSizeClass(size),
            className,
          )}
          aria-label={ariaLabel || `Błąd ładowania ikony ${name}`}
          title={`Nie można załadować ikony: ${name}`}
        >
          ?
        </div>
      );
    }

    // Loading state
    if (!IconComponent) {
      return (
        <div
          className={clsx(
            'inline-flex justify-center items-center',
            'bg-gray-100 rounded animate-pulse',
            getSizeClass(size),
            className,
          )}
          aria-label="Ładowanie ikony..."
          aria-hidden="true"
        />
      );
    }

    return (
      <div
        className={wrapperClasses}
        style={inlineStyles}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-label={ariaLabel}
        aria-hidden={ariaHidden}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
            : undefined
        }
      >
        {useStyleInline && (
          <style>{`
            svg#${componentId}  path {
              fill: ${fillClass};
            }
          `}</style>
        )}
        <IconComponent
          id={componentId}
          width={effectiveWidth}
          height={effectiveHeight}
          className={clsx(fillClass, 'h-full')}
          aria-hidden="true"
        />
      </div>
    );
  },
);

// Dodanie displayName dla lepszego debugowania
SVGIcon.displayName = 'SVGIcon';
